import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MoveHorizontal } from "lucide-react";
import { Eyebrow, Reveal } from "../Reveal";
import { IMAGES } from "../../data/site";

export const BeforeAfter = () => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <section data-testid="before-after-section" className="grain section-pad relative bg-charcoal">
      <div className="container-cando relative">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal className="flex justify-center">
            <Eyebrow dark>Before &amp; After</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2 mt-6 text-warm">See the Difference.</h2>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mx-auto mt-14 max-w-5xl">
          <div
            ref={ref}
            data-testid="before-after-slider"
            className="relative aspect-[4/3] w-full cursor-ew-resize select-none overflow-hidden sm:aspect-[16/9]"
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              update(e.clientX);
            }}
            onPointerMove={(e) => dragging.current && update(e.clientX)}
            onPointerUp={() => (dragging.current = false)}
          >
            <img
              src={IMAGES.after}
              alt="Finished renovation by Cando Construction"
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
              <img
                src={IMAGES.before}
                alt="Space before renovation"
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
            </div>

            <span className="absolute left-5 top-5 bg-charcoal/80 px-3 py-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.2em] text-warm">
              Before
            </span>
            <span className="absolute right-5 top-5 bg-bronze px-3 py-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.2em] text-charcoal">
              After
            </span>

            <div className="absolute inset-y-0" style={{ left: `${pos}%` }} data-testid="before-after-handle">
              <div className="absolute inset-y-0 -translate-x-1/2 border-l-2 border-warm" />
              <div className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-warm text-charcoal shadow-xl">
                <MoveHorizontal size={20} />
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mx-auto mt-12 max-w-2xl text-center">
          <p className="body-lg text-warm/70">
            From unfinished spaces to finished rooms, we focus on the details that make a renovation
            feel complete.
          </p>
          <Link to="/#projects" data-testid="before-after-cta" className="btn btn-outline-light mt-8">
            View All Projects
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
